import * as React from 'react';

import {
  Button as AriaButton,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  composeRenderProps,
  Focusable,
  type TooltipProps as AriaTooltipProps,
  type TooltipTriggerComponentProps as TooltipTriggerProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

export const TooltipTrigger = AriaTooltipTrigger;

/**
 * Tooltip component for displaying additional information on hover.
 * Creates an accessible tooltip that appears when the user hovers over
 * or focuses on the trigger element.
 *
 * @example
 * ```tsx
 * <TooltipTrigger>
 *   <Button icon={<InfoIcon />}>Info</Button>
 *   <Tooltip>Detailed information about this action</Tooltip>
 * </TooltipTrigger>
 * ```
 *
 * @param props - Tooltip properties
 * @param props.offset - Distance in pixels between the tooltip and its trigger element (default: 4)
 * @deprecated use Tooltip instead
 */
export const RawTooltip = ({
  className,
  offset = 4,
  ...props
}: AriaTooltipProps) => (
  <AriaTooltip
    offset={offset}
    className={composeRenderProps(className, (className) =>
      cn(
        '@RawTooltip',
        '@Tooltip',
        'z-50 animate-in overflow-hidden rounded-md border border-foreground/10 bg-space px-3 py-1.5 text-sm text-foreground shadow-md fade-in-0',
        'rac-entering:zoom-in-95',
        'rac-exiting:animate-out rac-exiting:fade-out-0 rac-exiting:zoom-out-95',
        'rac-placement-left:slide-in-from-right-2 rac-placement-right:slide-in-from-left-2 rac-placement-top:slide-in-from-bottom-2 rac-placement-bottom:slide-in-from-top-2',
        className,
      ),
    )}
    {...props}
  />
);

type TooltipProps = TooltipTriggerProps & {
  tip: React.ReactNode;
  children: React.ReactNode;
  placement?: AriaTooltipProps['placement'];
  wrapWithButton?: boolean;
  className?: string;
};

export const Tooltip = ({
  delay,
  children,
  tip,
  placement,
  wrapWithButton: wrapTriggerWithButton = true,
  className,
  ...props
}: TooltipProps) => (
  <TooltipTrigger delay={delay ?? 500} {...props}>
    {wrapTriggerWithButton ? (
      <AriaButton className={cn('border-none p-0 outline-none', className)}>
        {children}
      </AriaButton>
    ) : (
      <Focusable>
        <span role="button" tabIndex={0} className={className}>
          {children}
        </span>
      </Focusable>
    )}
    <RawTooltip className={'bg-neutral text-white'} placement={placement}>
      {tip}
    </RawTooltip>
  </TooltipTrigger>
);
